/**
 * DexieAdapter — IndexedDB adapter for web mode (no Electron).
 *
 * Exposes the EXACT same API as SQLiteAdapter so all 87+ components
 * work without any changes. Uses Dexie.js for IndexedDB access.
 *
 * Optimizations over basic Dexie:
 * - In-memory search index for fast product search (replaces FTS5)
 * - Bulk operations use Dexie transactions
 * - Compound indexes for common query patterns
 */

import Dexie from "dexie";
import { guardOrderIdentityBeforeWrite } from "../utils/cartIdentityGuard";
import { markLocalDbReset, reportLocalTillDataLoss } from "../utils/localTillDataLoss";

// Same table definitions as SQLiteAdapter — Dexie uses the same index syntax
const stores = {
  tillRegistrations: "++id",
  productCategories: "mProductCategoryId",
  productBrands: "brandId",
  newBrands: "brandId",
  dataSyncSummary: "++id, syncId, syncType",
  products: "++id, mProductId, mProductCategoryId, value, name, upc, brandId, isactive, productSegment, *batchIndex, *upcIndex",
  orders: "sOrderID, orderTime, documentno, isSynced, tillSessionId, customerSearchKey",
  pricingRules: "++id, billAmount, mPricingrulesId",
  posSaletypes: "++id, cwrSaletypeId",
  productUom: "++id, csUomId",
  tillEvents: "++id, tillSessionId, isSynced, tillStatus",
  logConfiguration: "++id",
  logInformation: "++id",
  restaurantTables: "++id",
  cashInCashOut: "++id",
  rfidData: "tag_value",
  tableData: "cwrFbTableId, name, tableSync",
  fbOrderData: "++id,fbOrderId,cwrFbTableId,fbOrderStatus,fbOrderSync",
  sectionTables: "++id",
  loyalityData: "loyaltylevelId",
  AllProductCategories: "mProductCategoryId",
  POSWorkFlowRules: "cwrRulesId",
  approvers: "++id",
  giftCardData: "cwrGiftcardTypeId",
  getExpenseCategory: "finExpCategoryId",
  getFinancialAccounts: "fin_financial_acct_id",
  paymentsData: "++id, tillSessionId, orderTime, tillStatus",
  ordersData: "++id, tillSessionId, orderTime, tillStatus",
  logActivity: "++id",
  salesRep: "s_salesrep_id, name,code",
  docTypesData: "++cs_doctype_id",
  getCompanionRules: "main_product_id",
  getRevenueCategories: "revenueCategoryId",
  pricingPolicies: "++id, dimensionCode, dimensionValue",
};

// ── In-Memory Search Index (replaces SQLite FTS5) ──
// Built on first search or after bulkPut to products table.
// Provides instant search without scanning all IndexedDB rows.
let _searchIndex = null;
let _searchIndexDirty = true;

function markSearchDirty() { _searchIndexDirty = true; }

// ── UOM Map Cache ──
// Products may be stored in IndexedDB with uomData:[] (pre-RC1-fix sync) or
// uomData:"" (SQLite adapter). Re-join from productUom at every read so that
// ALL paths (search, barcode scan via toArray(), direct get()) get correct
// isDecimalQty / isQtyDesimal values — not just the search-index path.
let _dexieUomMap = null;
let _dexieUomMapDirty = true;

function markDexieUomMapDirty() { _dexieUomMapDirty = true; _dexieUomMap = null; }

async function getDexieUomMap(dexieDb) {
  if (_dexieUomMap && !_dexieUomMapDirty) return _dexieUomMap;
  const uoms = await dexieDb.productUom.toArray();
  const map = new Map();
  for (const uom of uoms) {
    if (!map.has(uom.csUomId)) map.set(uom.csUomId, []);
    map.get(uom.csUomId).push(uom);
  }
  _dexieUomMap = map;
  _dexieUomMapDirty = false;
  return map;
}

function _enrichOne(p, uomMap) {
  if (!p) return p;
  const ud = p.uomData;
  if (ud && Array.isArray(ud) && ud.length > 0) return p;
  const uomData = uomMap.get(p.csUomId) || [];
  return uomData.length > 0 ? { ...p, uomData } : p;
}

async function enrichProducts(products, dexieDb) {
  if (!products || products.length === 0) return products;
  const needsEnrich = products.some(p => { const ud = p.uomData; return !ud || ud === "" || (Array.isArray(ud) && ud.length === 0); });
  if (!needsEnrich) return products;
  const uomMap = await getDexieUomMap(dexieDb);
  return products.map(p => _enrichOne(p, uomMap));
}

async function enrichProduct(product, dexieDb) {
  if (!product) return product;
  const ud = product.uomData;
  if (ud && Array.isArray(ud) && ud.length > 0) return product;
  const uomMap = await getDexieUomMap(dexieDb);
  return _enrichOne(product, uomMap);
}

async function getSearchIndex(dexieDb) {
  if (_searchIndex && !_searchIndexDirty) return _searchIndex;

  // Fetch products and UOM map in parallel. getDexieUomMap() is cached so the
  // productUom table is only read once per session (invalidated on any write).
  const [products, uomMap] = await Promise.all([
    dexieDb.products.toArray(),
    getDexieUomMap(dexieDb),
  ]);
  _searchIndex = products
    .filter((p) => p.isactive == true)
    .map((p) => {
      const ref = _enrichOne(p, uomMap);
      return {
        id: p.id,
        mProductId: p.mProductId,
        _name: (p.name || "").toLowerCase(),
        _value: (p.value || "").toLowerCase(),
        _upc: (p.upc || "").toLowerCase(),
        _batchIndex: (Array.isArray(p.batchIndex) ? p.batchIndex.join(" ") : String(p.batchIndex || "")).toLowerCase(),
        mProductCategoryId: p.mProductCategoryId,
        brandId: p.brandId,
        ref,
      };
    });
  _searchIndexDirty = false;
  return _searchIndex;
}

/**
 * Wrapper around a Dexie table to match SQLiteAdapter's Table interface.
 */
class TableWrapper {
  constructor(dexieTable, tableName, dexieDb) {
    this._table = dexieTable;
    this._name = tableName;
    this._db = dexieDb;
  }

  get name() { return this._name; }

  async toArray() {
    const results = await this._table.toArray();
    if (this._name === "products") return enrichProducts(results, this._db);
    return results;
  }
  async get(key) {
    const result = await this._table.get(key);
    if (this._name === "products") return enrichProduct(result, this._db);
    return result;
  }
  async add(data) {
    if (this._name === "productUom") markDexieUomMapDirty();
    return this._table.add(data);
  }

  async put(data, key) {
    if (this._name === "productUom") markDexieUomMapDirty();
    if (this._name === "orders") {
      await guardOrderIdentityBeforeWrite(
        data,
        (sOrderID) => this._db.table("orders").get(sOrderID),
        async (entry) => {
          try {
            const { logSequenceEvent } = await import("../utils/sequenceAudit");
            const { event, ...details } = entry;
            logSequenceEvent(event, details, { sequenceAuditLog: { add: (e) => this._db.table("sequenceAuditLog").add(e) } });
          } catch {
            // best-effort — the console.warn in guardOrderIdentityBeforeWrite already recorded this
          }
        }
      );
    }
    return this._table.put(data, key);
  }
  async update(key, changes) {
    if (this._name === "productUom") markDexieUomMapDirty();
    return this._table.update(key, changes);
  }
  async delete(key) { return this._table.delete(key); }
  async clear() {
    if (this._name === "products") markSearchDirty();
    if (this._name === "productUom") markDexieUomMapDirty();
    return this._table.clear();
  }
  async count() { return this._table.count(); }

  // Explicit search-index invalidation for single-row writes that bypass the
  // bulk paths (add/put/update and raw Collection.modify()). syncSingleProductByValue
  // writes one product at a time, so without this the in-memory index stays stale
  // and the freshly fetched product is invisible until the next bulk sync/reload.
  invalidateSearchIndex() { if (this._name === "products") markSearchDirty(); }

  async bulkPut(items) {
    if (this._name === "products") markSearchDirty();
    if (this._name === "productUom") markDexieUomMapDirty();
    // Use transaction for better performance on large batches
    if (items.length > 100) {
      return this._db.transaction("rw", this._table, () => this._table.bulkPut(items));
    }
    return this._table.bulkPut(items);
  }

  async bulkAdd(items) {
    if (this._name === "products") markSearchDirty();
    if (this._name === "productUom") markDexieUomMapDirty();
    return this._table.bulkPut(items); // bulkPut is safer (no fail on duplicate)
  }

  async bulkUpsert(items, keyField) {
    if (!items || items.length === 0) return;
    if (this._name === "products") markSearchDirty();

    // Without a keyField, primary-key collisions are the only upsert path.
    if (!keyField) return this._table.bulkPut(items);

    // Real upsert by non-primary keyField (e.g. "value" for products).
    // The products table uses `++id` as primary key and incoming rows have no
    // `id`, so a naive bulkPut would insert a duplicate on every delta sync.
    // Look up existing rows by the natural key, preserve their primary key,
    // and merge — that's a true upsert.
    return this._db.transaction("rw", this._table, async () => {
      const keys = items.map((it) => it[keyField]).filter((k) => k != null);
      const existing = keys.length > 0 ? await this._table.where(keyField).anyOf(keys).toArray() : [];
      const byKey = new Map(existing.map((r) => [r[keyField], r]));
      const merged = items.map((item) => {
        const old = byKey.get(item[keyField]);
        return old ? { ...old, ...item, id: old.id } : item;
      });
      return this._table.bulkPut(merged);
    });
  }

  where(fieldOrObj) {
    if (typeof fieldOrObj === "object") {
      let collection = this._table.toCollection();
      for (const [key, val] of Object.entries(fieldOrObj)) {
        collection = collection.and((item) => item[key] === val);
      }
      return new CollectionWrapper(collection, this._name, this._db);
    }
    return new WhereClauseWrapper(this._table, fieldOrObj, this._name, this._db);
  }

  filter(fn) { return new CollectionWrapper(this._table.filter(fn), this._name, this._db); }
  orderBy(field) { return new CollectionWrapper(this._table.orderBy(field), this._name, this._db); }
  limit(n) { return new CollectionWrapper(this._table.limit(n), this._name, this._db); }
  reverse() { return new CollectionWrapper(this._table.reverse(), this._name, this._db); }
  toCollection() { return new CollectionWrapper(this._table.toCollection(), this._name, this._db); }

  /**
   * Product search — uses in-memory index for instant results.
   * Falls back to Dexie filter for non-product tables.
   */
  async search({ searchTerm, limit = 50, isSmartSearch, categoryId, brandId }) {
    if (!searchTerm) return this._table.limit(limit).toArray();

    const term = searchTerm.toLowerCase();
    const index = await getSearchIndex(this._db);

    let results = index.filter((p) => {
      return p._name.includes(term) || p._value.includes(term) || p._upc.includes(term) || p._batchIndex.includes(term);
    });

    if (categoryId) results = results.filter((p) => p.mProductCategoryId === categoryId);
    if (brandId) results = results.filter((p) => p.brandId === brandId);

    return results.slice(0, limit).map((p) => p.ref);
  }
}

/**
 * WhereClause wrapper — matches SQLiteAdapter's WhereClause interface.
 */
class WhereClauseWrapper {
  constructor(table, field, tableName = null, dexieDb = null) {
    this._table = table;
    this._field = field;
    this._tableName = tableName;
    this._db = dexieDb;
    this._dexieWhere = table.where(field);
  }

  // Guard: Dexie rejects null/undefined/object keys — return empty collection instead of crashing
  _safe(value) { return value != null && typeof value !== "object"; }
  _wrap(c) { return new CollectionWrapper(c, this._tableName, this._db); }

  equals(value) { return this._safe(value) ? this._wrap(this._dexieWhere.equals(value)) : CollectionWrapper.empty(); }
  notEqual(value) { return this._safe(value) ? this._wrap(this._dexieWhere.notEqual(value)) : CollectionWrapper.empty(); }
  above(value) { return this._safe(value) ? this._wrap(this._dexieWhere.above(value)) : CollectionWrapper.empty(); }
  aboveOrEqual(value) { return this._safe(value) ? this._wrap(this._dexieWhere.aboveOrEqual(value)) : CollectionWrapper.empty(); }
  below(value) { return this._safe(value) ? this._wrap(this._dexieWhere.below(value)) : CollectionWrapper.empty(); }
  belowOrEqual(value) { return this._safe(value) ? this._wrap(this._dexieWhere.belowOrEqual(value)) : CollectionWrapper.empty(); }
  between(lower, upper, includeLower = true, includeUpper = false) {
    return this._wrap(this._dexieWhere.between(lower, upper, includeLower, includeUpper));
  }
  anyOf(values) { return Array.isArray(values) && values.length > 0 ? this._wrap(this._dexieWhere.anyOf(values)) : CollectionWrapper.empty(); }
  noneOf(values) { return Array.isArray(values) && values.length > 0 ? this._wrap(this._dexieWhere.noneOf(values)) : CollectionWrapper.empty(); }
  startsWith(prefix) { return this._wrap(this._dexieWhere.startsWith(prefix)); }
  startsWithAnyOf(prefixes) { return this._wrap(this._dexieWhere.startsWithAnyOf(prefixes)); }

  equalsIgnoreCase(value) {
    const lower = (value || "").toLowerCase();
    return this._wrap(this._table.filter((item) => (item[this._field] || "").toString().toLowerCase() === lower));
  }

  startsWithIgnoreCase(prefix) {
    const lower = (prefix || "").toLowerCase();
    return this._wrap(this._table.filter((item) => (item[this._field] || "").toString().toLowerCase().startsWith(lower)));
  }

  startsWithAnyOfIgnoreCase(prefixes) {
    const lowerPrefixes = (prefixes || []).map((p) => p.toLowerCase());
    return this._wrap(this._table.filter((item) => {
      const val = (item[this._field] || "").toString().toLowerCase();
      return lowerPrefixes.some((p) => val.startsWith(p));
    }));
  }
}

/**
 * Collection wrapper — matches SQLiteAdapter's Collection interface.
 */
class CollectionWrapper {
  constructor(dexieCollection, tableName = null, dexieDb = null) {
    this._collection = dexieCollection;
    this._tableName = tableName;
    this._db = dexieDb;
  }

  // Empty collection — returned when query key is invalid (null/undefined)
  static empty() {
    return {
      toArray: async () => [],
      first: async () => undefined,
      count: async () => 0,
      keys: async () => [],
      sortBy: async () => [],
      modify: async () => 0,
      delete: async () => 0,
      filter: () => CollectionWrapper.empty(),
      and: () => CollectionWrapper.empty(),
      limit: () => CollectionWrapper.empty(),
      offset: () => CollectionWrapper.empty(),
      reverse: () => CollectionWrapper.empty(),
    };
  }

  async toArray() {
    const results = await this._collection.toArray();
    if (this._tableName === "products" && this._db) return enrichProducts(results, this._db);
    return results;
  }
  async first() {
    const result = await this._collection.first();
    if (this._tableName === "products" && this._db) return enrichProduct(result, this._db);
    return result;
  }
  async count() { return this._collection.count(); }
  async keys() { return this._collection.keys(); }
  async sortBy(field) { return this._collection.sortBy(field); }

  async modify(changesOrFn) {
    return this._collection.modify(changesOrFn);
  }

  async delete() { return this._collection.delete(); }

  filter(fn) { return new CollectionWrapper(this._collection.filter(fn), this._tableName, this._db); }
  and(fn) { return new CollectionWrapper(this._collection.and(fn), this._tableName, this._db); }
  limit(n) { return new CollectionWrapper(this._collection.limit(n), this._tableName, this._db); }
  offset(n) { return new CollectionWrapper(this._collection.offset(n), this._tableName, this._db); }
  reverse() { return new CollectionWrapper(this._collection.reverse(), this._tableName, this._db); }

  or(field) {
    try {
      const dexieOr = this._collection.or(field);
      return new WhereClauseOrWrapper(dexieOr, this._tableName, this._db);
    } catch {
      const table = this._collection._ctx?.table || this._collection;
      return new WhereClauseWrapper(table, field, this._tableName, this._db);
    }
  }
}

/**
 * WhereClause wrapper for Dexie's .or() result.
 */
class WhereClauseOrWrapper {
  constructor(dexieWhereClause, tableName = null, dexieDb = null) {
    this._wc = dexieWhereClause;
    this._tableName = tableName;
    this._db = dexieDb;
  }
  _wrap(c) { return new CollectionWrapper(c, this._tableName, this._db); }
  equals(value) { return this._wrap(this._wc.equals(value)); }
  notEqual(value) { return this._wrap(this._wc.notEqual(value)); }
  above(value) { return this._wrap(this._wc.above(value)); }
  aboveOrEqual(value) { return this._wrap(this._wc.aboveOrEqual(value)); }
  below(value) { return this._wrap(this._wc.below(value)); }
  belowOrEqual(value) { return this._wrap(this._wc.belowOrEqual(value)); }
  anyOf(values) { return this._wrap(this._wc.anyOf(values)); }
  noneOf(values) { return this._wrap(this._wc.noneOf(values)); }
  between(lower, upper, il, iu) { return this._wrap(this._wc.between(lower, upper, il, iu)); }
  startsWith(prefix) { return this._wrap(this._wc.startsWith(prefix)); }
  startsWithAnyOf(prefixes) { return this._wrap(this._wc.startsWithAnyOf(prefixes)); }
  equalsIgnoreCase(value) { return this._wrap(this._wc.equals(value)); }
}

/**
 * Create the DexieAdapter — exposes same API as createSQLiteAdapter().
 */
export function createDexieAdapter() {
  const dexieDb = new Dexie("cwpos");

  // ── Schema Versioning ──
  // Dexie requires ALL previous versions declared for upgrade path.
  // When adding tables/indexes: add new version, keep all previous.
  // Existing data in unchanged tables is preserved automatically.
  //
  // Version history:
  //   1 — Initial: 31 tables (original release)
  //   2 — Added: getCompanionRules, getRevenueCategories (33 tables)
  //   6 — Added: sequenceAuditLog (34 tables) — see note at dexieDb.version(6) below

  // v1: original schema (needed for upgrade path from existing DBs)
  const v1Stores = { ...stores };
  delete v1Stores.getCompanionRules;
  delete v1Stores.getRevenueCategories;
  dexieDb.version(1).stores(v1Stores);

  // v2: current schema (adds new tables — Dexie auto-creates them)
  dexieDb.version(2).stores(stores);

  // v3: same schema, but run a one-shot dedup of `products` by `value`.
  // Older builds had a broken bulkUpsert that ignored the keyField and called
  // bulkPut, so every delta sync inserted new rows under a fresh ++id even when
  // a row with that `value` already existed. Result: many duplicates, stale
  // copies winning the search index limit. This upgrade collapses each value
  // group to its latest row (highest id).
  // Done defensively (separate statements + optional chaining) so the mocked
  // Dexie used in unit tests — which returns no Version handle from
  // `.stores()` — doesn't blow up adapter construction.
  const v3 = dexieDb.version(3).stores(stores);
  if (v3 && typeof v3.upgrade === "function") {
    v3.upgrade(async (tx) => {
      try {
        const all = await tx.table("products").toArray();
        const byValue = new Map();
        const toDelete = [];
        all.sort((a, b) => (b.id || 0) - (a.id || 0));
        for (const p of all) {
          const key = p.value;
          if (key == null) continue;
          if (byValue.has(key)) toDelete.push(p.id);
          else byValue.set(key, p);
        }
        if (toDelete.length > 0) {
          await tx.table("products").bulkDelete(toDelete);
        }
      } catch (err) {
        console.warn("[DexieAdapter] v3 dedup upgrade skipped:", err?.message);
      }
    });
  }

  // v4: add compound indexes for common query patterns
  const v4Stores = { ...stores };
  v4Stores.products += ", [isactive+mProductCategoryId]";
  v4Stores.orders += ", [isSynced+tillSessionId]";
  dexieDb.version(4).stores(v4Stores);

  // v5: add pricingPolicies table for pricing policy sync
  const v5Stores = { ...v4Stores };
  v5Stores.pricingPolicies = "++id, dimensionCode, dimensionValue";
  dexieDb.version(5).stores(v5Stores);

  // v6: add sequenceAuditLog — durable forensic trail for document-number
  // events (init/consume/di31-recovery/stale-identity-recovery/preview-shown).
  // Previously this only lived in localStorage (seqAuditTrail), which is wiped
  // by several unrelated code paths (ErrorBoundary, ManagerApproval cache-bust,
  // appVersionGate's version-bump cleanup, ad-hoc "Clear Cache" buttons) — none
  // of which touch IndexedDB tables. Living here, the trail survives all of them.
  const v6Stores = { ...v5Stores };
  v6Stores.sequenceAuditLog = "++id, ts, event";
  dexieDb.version(6).stores(v6Stores);

  // Auto-open the database (Dexie lazy-opens on first query, but explicit open
  // catches schema errors early and logs them)
  dexieDb
    .open()
    .then(() =>
      // BUG-2582: record a deleted database / a remembered till without its data.
      reportLocalTillDataLoss({ tillRegistrations: dexieDb.tillRegistrations, tillEvents: dexieDb.tillEvents, sequenceAuditLog: dexieDb.sequenceAuditLog })
    )
    .catch((err) => {
    console.error("[DexieAdapter] Failed to open IndexedDB:", err.message);
    // If upgrade fails (e.g., version conflict), delete and recreate
    if (err.name === "VersionError" || err.name === "UpgradeError") {
      console.warn("[DexieAdapter] Deleting corrupt DB and recreating...");
      markLocalDbReset(err);
      Dexie.delete("cwpos").then(() => {
        window.location.reload();
      });
    }
  });

  const adapter = {
    _dexie: dexieDb,
    tables: [],
    isOpen: () => dexieDb.isOpen(),
    open: () => dexieDb.open(),
    close: () => dexieDb.close(),
    on: () => {},
    table: (name) => adapter[name],
    transaction: async (mode, tableNames, callback) => {
      const tables = tableNames.map((name) => dexieDb[name]);
      return dexieDb.transaction(mode, ...tables, callback);
    },
  };

  for (const name of Object.keys(v6Stores)) {
    const wrapped = new TableWrapper(dexieDb[name], name, dexieDb);
    adapter[name] = wrapped;
    adapter.tables.push(wrapped);
  }

  return adapter;
}
